import React, { useState, useEffect } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { showToast } from "../../utils/customToast";
import FilterBar from "./DashboardComponents/FilterBar";
import StatCards from "./DashboardComponents/StatCards";
import PerformanceChart from "./DashboardComponents/PerformanceChart";
import DutySlipPieChart from "./DashboardComponents/DutySlipPieChart";
import FinancialOverview from "./DashboardComponents/FinancialOverview";
import UpcomingBookings from "./DashboardComponents/UpcomingBookings";
import DutySlipActivity from "./DashboardComponents/DutySlipActivity";
import CompanyPerformance from "./DashboardComponents/CompanyPerformance";
import ConversionFunnel from "./DashboardComponents/ConversionFunnel";
import RecentActivity from "./DashboardComponents/RecentActivity";
import SmartInsights from "./DashboardComponents/SmartInsights";
import PdfPreviewModal from "./DashboardComponents/PdfPreviewModal";
import { downloadDutySlip, downloadInvoice } from "../../utils/dashboardPdfHelper";
import { useCompany } from "../../context/CompanyContext";
import { DashboardSkeleton } from "../Skeleton";
import "./Dashboard.css";

const BASE_API_URL =
  (process.env.REACT_APP_BASE_API_URL || "http://localhost:5000/api").replace(
    /\/api\/?$/,
    ""
  ) + "/api";

export default function Dashboard() {
  const { selectedCompany } = useCompany();

  const [selectedCompanyId, setSelectedCompanyId] = useState(() => {
    if (selectedCompany?.id || selectedCompany?.CompID) {
      return selectedCompany.id || selectedCompany.CompID;
    }
    const saved = localStorage.getItem("selectedCompany");
    if (saved) {
      try {
        const obj = JSON.parse(saved);
        return obj.id || obj.CompID || 1147;
      } catch (e) {}
    }
    return localStorage.getItem("companyId") || 1147;
  });

  const [companyName, setCompanyName] = useState(() => {
    if (selectedCompany?.name || selectedCompany?.Name) {
      return selectedCompany.name || selectedCompany.Name;
    }
    const saved = localStorage.getItem("selectedCompany");
    if (saved) {
      try {
        const obj = JSON.parse(saved);
        return obj.name || obj.Name || "DAZY TOURE TRAVEL'S";
      } catch (e) {}
    }
    return "DAZY TOURE TRAVEL'S";
  });

  const getDefaultDates = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return {
      dateFrom: `${y}-${m}-01`,
      dateTo: `${y}-${m}-${d}`,
    };
  };

  const defaults = getDefaultDates();

  const [filters, setFilters] = useState({
    dateFrom: defaults.dateFrom,
    dateTo: defaults.dateTo,
    company: "All Companies",
    partyCode: "",
    dutySlipStatus: "",
    invoiceStatus: "",
  });

  const [activePerfTab, setActivePerfTab] = useState("This Month");
  const [companyTab, setCompanyTab] = useState("Bookings");

  // PDF Preview Modal state
  const [previewModal, setPreviewModal] = useState({
    open: false,
    type: "",
    data: null,
  });

  // Sync with CompanyContext
  useEffect(() => {
    if (selectedCompany) {
      const newId = selectedCompany.id || selectedCompany.CompID;
      const newName = selectedCompany.name || selectedCompany.Name;
      if (newId) {
        setSelectedCompanyId((prevId) => {
          if (String(prevId) !== String(newId)) {
            setFilters((prev) => ({ ...prev, partyCode: "" }));
            return newId;
          }
          return prevId;
        });
      }
      if (newName) {
        setCompanyName(newName);
      }
    }
  }, [selectedCompany]);

  // Sync with cross-tab storage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "selectedCompany" && e.newValue) {
        try {
          const obj = JSON.parse(e.newValue);
          if (obj.id) {
            setSelectedCompanyId(obj.id);
            setFilters((prev) => ({ ...prev, partyCode: "" }));
          }
          if (obj.name) setCompanyName(obj.name);
        } catch (err) {}
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // React Query: Auto-cached, instant load on revisit, background refresh, auto-retry on drop
  const {
    data: dashData = null,
    isLoading: loading,
    refetch: fetchDashboardData,
  } = useQuery({
    queryKey: ["dashboardSummary", selectedCompanyId, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.append("dateTo", filters.dateTo);
      if (filters.partyCode) params.append("partyCode", filters.partyCode);
      if (filters.dutySlipStatus) params.append("dutySlipStatus", filters.dutySlipStatus);
      if (filters.invoiceStatus) params.append("invoiceStatus", filters.invoiceStatus);

      const url = `${BASE_API_URL}/dashboard/summary/${selectedCompanyId}?${params.toString()}`;
      const res = await axios.get(url);

      if (res.data && res.data.success) {
        if (res.data.data?.companyName) {
          setCompanyName(res.data.data.companyName);
        }
        return res.data.data;
      }
      return null;
    },
    enabled: Boolean(selectedCompanyId),
  });

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    const reset = {
      dateFrom: defaults.dateFrom,
      dateTo: defaults.dateTo,
      company: "All Companies",
      partyCode: "",
      dutySlipStatus: "",
      invoiceStatus: "",
    };
    setFilters(reset);
    fetchDashboardData(reset);
    showToast.info("Filters reset to current month");
  };

  // Preview Handlers (Open Modal)
  const handlePreviewDutySlip = (item) => {
    setPreviewModal({ open: true, type: "dutyslip", data: item });
  };

  const handlePreviewInvoice = (item) => {
    setPreviewModal({ open: true, type: "invoice", data: item });
  };

  // Progress-enabled Downloader
  const handleDownloadWithProgress = async (type, item) => {
    try {
      if (type === "dutyslip") {
        await downloadDutySlip(item);
      } else {
        await downloadInvoice(item);
      }
    } catch (err) {
      console.warn("Download error:", err);
    }
  };


  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-breadcrumb">
        <span>Dashboard &gt;</span> {companyName || "Overview"}
      </div>

      {/* 1. Filter Bar */}
      <FilterBar
        filters={filters}
        setFilters={setFilters}
        onFilterChange={handleFilterChange}
        onReset={handleResetFilters}
        parties={dashData?.parties || []}
      />

      {loading && !dashData ? (
        <DashboardSkeleton />
      ) : (
        <>
          {/* 2. Top 8 Stat Cards */}
          <StatCards stats={dashData?.stats} />

      {/* 3. Middle Row: Main Performance Chart + Donut + Financial Overview */}
      <div className="row g-2 mb-2">
        <div className="col-lg-6 col-12">
          <PerformanceChart
            chartData={dashData?.chartData}
            activeTab={activePerfTab}
            setActiveTab={setActivePerfTab}
          />
        </div>
        <div className="col-lg-3 col-md-6 col-12">
          <DutySlipPieChart
            invoiced={dashData?.dutySlipStatus?.invoiced ?? 0}
            notInvoiced={dashData?.dutySlipStatus?.notInvoiced ?? 0}
            dutySlipStatus={dashData?.dutySlipStatus}
          />
        </div>
        <div className="col-lg-3 col-md-6 col-12">
          <FinancialOverview
            revenue={dashData?.financial?.revenue || "₹0"}
            avgInvoiceValue={dashData?.financial?.avgInvoiceValue || "₹0"}
            highestInvoice={dashData?.financial?.highestInvoice || "₹0"}
            monthlyData={dashData?.financial?.monthlyData || []}
          />
        </div>
      </div>

      {/* 4. Tables Row: Upcoming Bookings + Duty Slip Activity + Company Performance */}
      <div className="row g-2 mb-2">
        <div className="col-lg-4 col-md-6 col-12">
          <UpcomingBookings
            bookings={dashData?.upcomingBookings || []}
            onPreviewDutySlip={handlePreviewDutySlip}
            onDownloadWithProgress={handleDownloadWithProgress}
          />
        </div>
        <div className="col-lg-4 col-md-6 col-12">
          <DutySlipActivity
            dutySlips={dashData?.recentDutySlips || []}
            totalCount={dashData?.dutySlipStatus?.total ?? dashData?.stats?.totalDutySlips?.value ?? 0}
            invoicedCount={dashData?.dutySlipStatus?.invoiced ?? 0}
            pendingCount={dashData?.dutySlipStatus?.notInvoiced ?? 0}
            onPreviewDutySlip={handlePreviewDutySlip}
            onDownloadWithProgress={handleDownloadWithProgress}
          />
        </div>
        <div className="col-lg-4 col-12">
          <CompanyPerformance
            activeTab={companyTab}
            setActiveTab={setCompanyTab}
            companyComparison={dashData?.companyComparison || []}
          />
        </div>
      </div>

      {/* 5. Bottom Row: Conversion Funnel + Recent Activity + Smart Insights */}
      <div className="row g-2">
        <div className="col-lg-4 col-12">
          <ConversionFunnel
            bookings={dashData?.conversionFunnel?.bookings ?? 0}
            dutySlips={dashData?.conversionFunnel?.dutySlips ?? 0}
            invoices={dashData?.conversionFunnel?.invoices ?? 0}
            conversionFunnel={dashData?.conversionFunnel}
          />
        </div>
        <div className="col-lg-5 col-md-8 col-12">
          <RecentActivity
            recentInvoices={dashData?.recentInvoices || []}
            onPreviewInvoice={handlePreviewInvoice}
            onDownloadWithProgress={handleDownloadWithProgress}
          />
        </div>
        <div className="col-lg-3 col-md-4 col-12">
          <SmartInsights insights={dashData?.insights || []} />
        </div>
      </div>
      </>
      )}

      {/* 6. Document Preview Modal */}
      <PdfPreviewModal
        isOpen={previewModal.open}
        type={previewModal.type}
        data={previewModal.data}
        onClose={() => setPreviewModal({ open: false, type: "", data: null })}
        onDownloadWithProgress={handleDownloadWithProgress}
      />
    </div>
  );
}
